import { create } from 'zustand';
import { coworkService, type ChatMessage, type ConversationSummary } from '@/services/cowork.service';
import { agentsService } from '@/services/agents.service';
import { knowledgeService } from '@/services/knowledge.service';

type AssistantMode = 'standard' | 'advanced';

export type PanelEntityType = 'agent' | 'agent_list' | 'knowledge_base' | 'knowledge_base_list' | 'training' | 'stats' | 'generic';

export interface PanelAction {
  entityType: PanelEntityType;
  entityData: any;
  toolName: string;
  timestamp: Date;
}

export interface PanelContext {
  isOpen: boolean;
  entityType: PanelEntityType | null;
  entityData: any;
  toolName: string | null;
  history: PanelAction[];
}

interface CoworkState {
  // Conversation list
  conversations: ConversationSummary[];
  activeConversationId: string | null;
  isLoadingConversations: boolean;

  // Current conversation messages
  messages: ChatMessage[];
  isLoading: boolean;
  isStreaming: boolean;
  error: string | null;

  // Mode
  mode: AssistantMode;
  setMode: (mode: AssistantMode) => void;

  // Action panel
  panel: PanelContext;
  openPanel: (entityType: PanelEntityType, entityData: any, toolName: string) => void;
  closePanel: () => void;
  refreshPanel: () => Promise<void>;
  injectPanelMessage: (text: string) => void;

  // Actions
  loadConversations: () => Promise<void>;
  createNewConversation: () => Promise<void>;
  selectConversation: (id: string) => Promise<void>;
  sendMessage: (message: string, context?: any) => Promise<void>;
  deleteConversation: (id: string) => Promise<void>;
  renameConversation: (id: string, title: string) => Promise<void>;
  clearError: () => void;

  // Legacy compat
  loadHistory: () => Promise<void>;
  clearHistory: () => Promise<void>;
}

// Map tool names to panel entity types
const TOOL_ENTITY_MAP: Record<string, PanelEntityType> = {
  create_agent: 'agent',
  get_agent: 'agent',
  update_agent: 'agent',
  delete_agent: 'agent',
  duplicate_agent: 'agent',
  update_agent_status: 'agent',
  get_agent_stats: 'stats',
  list_agents: 'agent_list',
  create_knowledge_base: 'knowledge_base',
  get_knowledge_base: 'knowledge_base',
  update_knowledge_base: 'knowledge_base',
  list_knowledge_bases: 'knowledge_base_list',
  list_knowledge_documents: 'knowledge_base',
  create_training_ground: 'training',
  get_dashboard_stats: 'stats',
  get_usage_by_agent: 'stats',
  get_pipeline: 'agent',
  add_pipeline_step: 'agent',
};

function resolveToolPanel(toolCalls: Array<{ tool: string; params: any; result: any }>): PanelAction | null {
  // Walk backwards — last actionable tool is most relevant
  for (let i = toolCalls.length - 1; i >= 0; i--) {
    const tc = toolCalls[i];
    if (!tc.result?.success) continue;
    const entityType = TOOL_ENTITY_MAP[tc.tool];
    if (entityType) {
      return { entityType, entityData: tc.result.data, toolName: tc.tool, timestamp: new Date() };
    }
  }
  // If no mapped tool, show generic for the last successful call (skip search_tools)
  for (let i = toolCalls.length - 1; i >= 0; i--) {
    const tc = toolCalls[i];
    if (tc.tool === 'search_tools') continue;
    if (tc.result?.success) {
      return { entityType: 'generic', entityData: tc.result.data, toolName: tc.tool, timestamp: new Date() };
    }
  }
  return null;
}

function parseRawMessages(messages: any[]): ChatMessage[] {
  const parsed: ChatMessage[] = [];

  // First pass: pair tool_use (assistant) with tool_result (next user msg) by position.
  // Build a map of assistant msg index → reconstructed tool_calls.
  const reconstructedToolCalls = new Map<number, Array<{ tool: string; params: any; result: any }>>();

  for (let i = 0; i < messages.length; i++) {
    const raw = messages[i].content;
    if (!Array.isArray(raw) || messages[i].role !== 'assistant' && messages[i].role !== 'model') continue;
    const uses = raw.filter((b: any) => b.type === 'tool_use');
    if (!uses.length) continue;

    // Look for tool_result in the next message
    const next = messages[i + 1];
    const nextResults = next && Array.isArray(next.content)
      ? next.content.filter((b: any) => b.type === 'tool_result')
      : [];

    const calls = uses.map((u: any, j: number) => {
      let resultData: any = null;
      if (nextResults[j]) {
        try { resultData = typeof nextResults[j].content === 'string' ? JSON.parse(nextResults[j].content) : nextResults[j].content; } catch { /* skip */ }
      }
      return { tool: u.name, params: u.input, result: resultData };
    });
    reconstructedToolCalls.set(i, calls);
  }

  // Pending tool_calls to attach to the next visible assistant message
  let pendingToolCalls: Array<{ tool: string; params: any; result: any }> | null = null;

  for (let idx = 0; idx < messages.length; idx++) {
    const msg = messages[idx];
    const rawContent = msg.content;
    let content = '';

    // Check if this assistant message has reconstructed tool_calls
    const reconstructed = reconstructedToolCalls.get(idx);
    if (reconstructed) {
      // Queue them — they'll attach to the next assistant message with text content
      pendingToolCalls = [...(pendingToolCalls || []), ...reconstructed];
    }

    if (typeof rawContent === 'string') {
      content = rawContent;
    } else if (Array.isArray(rawContent)) {
      const textBlocks = rawContent.filter((b: any) => b.type === 'text');
      const hasToolBlocks = rawContent.some((b: any) => b.type === 'tool_use' || b.type === 'tool_result');
      if (hasToolBlocks && textBlocks.length === 0) continue;
      content = textBlocks.map((b: any) => b.text).join('\n');
    }

    // Handle compact markers
    if (msg.type === 'compact') {
      parsed.push({
        id: msg.id || crypto.randomUUID(),
        role: 'system',
        type: 'compact',
        content: msg.content || '',
        compacted_count: msg.compacted_count,
        usage: msg.usage ? {
          input_tokens: msg.usage.input_tokens || 0,
          output_tokens: msg.usage.output_tokens || 0,
          cost_usd: msg.usage.cost_usd || 0,
        } : undefined,
        timestamp: new Date(msg.timestamp || msg.created_at || Date.now()),
      });
      continue;
    }

    if (!content) continue;

    const usage = msg.usage ? {
      input_tokens: msg.usage.input_tokens || 0,
      output_tokens: msg.usage.output_tokens || 0,
      cost_usd: msg.usage.cost_usd || 0,
    } : undefined;

    // Attach pending tool_calls to this assistant message
    let toolCalls = msg.tool_calls;
    if (!toolCalls && pendingToolCalls && (msg.role === 'assistant' || msg.role === 'model')) {
      toolCalls = pendingToolCalls;
      pendingToolCalls = null;
    }

    parsed.push({
      id: msg.id || crypto.randomUUID(),
      role: msg.role === 'model' ? 'assistant' : msg.role,
      content,
      tool_calls: toolCalls,
      usage,
      timestamp: new Date(msg.timestamp || msg.created_at || Date.now()),
    });
  }
  return parsed;
}

function buildStreamCallbacks(set: any, assistantId: string, afterDone?: () => void) {
  return {
    onModeSwitch: (data: { mode: string; auto: boolean }) => {
      if (data.auto) {
        // Insert a system message showing auto-switch
        set((state: CoworkState) => ({
          messages: [
            ...state.messages.filter(m => m.id !== assistantId),
            {
              id: crypto.randomUUID(),
              role: 'system' as const,
              type: 'mode_switch' as const,
              content: `Switched to ${data.mode === 'advanced' ? 'Advanced' : 'Standard'} mode for this task`,
              timestamp: new Date(),
            },
            state.messages.find(m => m.id === assistantId)!,
          ].filter(Boolean),
        }));
      }
    },
    onMode: (_data: { mode: string; model: string }) => {
      // Mode info — could display model name, but mainly used for done event
    },
    onTextDelta: (text: string) => {
      // Clear activeTools when text starts streaming — stage is done
      set((state: CoworkState) => ({
        messages: state.messages.map((m) =>
          m.id === assistantId ? { ...m, content: m.content + text, activeTools: [] } : m
        ),
      }));
    },
    onToolStart: (tool: string) => {
      set((state: CoworkState) => ({
        messages: state.messages.map((m) =>
          m.id === assistantId
            ? { ...m, activeTools: [...(m.activeTools || []), tool] }
            : m
        ),
      }));
    },
    onToolResult: (_data: { tool: string; success: boolean }) => {
      // Don't remove from activeTools — keep showing the stage label
      // until text_delta starts or done fires. This prevents the label
      // from flashing (tool_start → tool_result happens in <5ms).
    },
    onDone: (data: { tool_calls: any[]; usage: any; mode?: string }) => {
      // Resolve panel context from tool calls
      const panelAction = data.tool_calls?.length ? resolveToolPanel(data.tool_calls) : null;

      set((state: CoworkState) => {
        const newState: Partial<CoworkState> = {
          messages: state.messages.map((m) =>
            m.id === assistantId
              ? {
                  ...m,
                  tool_calls: data.tool_calls,
                  isStreaming: false,
                  activeTools: [],
                  usage: data.usage ? {
                    input_tokens: data.usage.input_tokens || 0,
                    output_tokens: data.usage.output_tokens || 0,
                    cost_usd: data.usage.cost_usd || 0,
                    mode: data.mode,
                  } : undefined,
                }
              : m
          ),
          isLoading: false,
          isStreaming: false,
        };

        // Auto-open panel if there's an actionable tool result
        if (panelAction) {
          newState.panel = {
            isOpen: true,
            entityType: panelAction.entityType,
            entityData: panelAction.entityData,
            toolName: panelAction.toolName,
            history: [...state.panel.history, panelAction],
          };
        }

        return newState;
      });
      afterDone?.();
    },
    onError: (error: string) => {
      set((state: CoworkState) => ({
        messages: state.messages.map((m) =>
          m.id === assistantId
            ? {
                ...m,
                content: m.content || (error.includes('Rate limited') ? '*Rate limited — please wait a moment and try again*' : '*Response interrupted*'),
                isStreaming: false,
              }
            : m
        ),
        error: null,
        isLoading: false,
        isStreaming: false,
      }));
    },
  };
}

export const useCoworkStore = create<CoworkState>((set, get) => ({
  conversations: [],
  activeConversationId: null,
  isLoadingConversations: false,

  messages: [],
  isLoading: false,
  isStreaming: false,
  error: null,

  mode: 'standard' as AssistantMode,
  setMode: (mode: AssistantMode) => set({ mode }),

  // Action panel
  panel: { isOpen: false, entityType: null, entityData: null, toolName: null, history: [] },
  openPanel: (entityType, entityData, toolName) => set((s) => ({
    panel: {
      isOpen: true,
      entityType,
      entityData,
      toolName,
      history: [...s.panel.history, { entityType, entityData, toolName, timestamp: new Date() }],
    },
  })),
  closePanel: () => set((s) => ({ panel: { ...s.panel, isOpen: false } })),
  refreshPanel: async () => {
    const { panel } = get();
    if (!panel.entityType || !panel.entityData) return;
    try {
      let freshData = panel.entityData;
      const id = panel.entityData?.id;
      if (!id) return;
      if (panel.entityType === 'agent') {
        freshData = await agentsService.getAgent(id);
      } else if (panel.entityType === 'knowledge_base') {
        freshData = await knowledgeService.getKnowledgeBase(id);
      }
      set((s) => ({ panel: { ...s.panel, entityData: freshData } }));
    } catch {
      // Keep existing data if refresh fails
    }
  },
  injectPanelMessage: (text: string) => {
    // Send a message from the panel action back into the conversation
    get().sendMessage(text);
  },

  loadConversations: async () => {
    set({ isLoadingConversations: true });
    try {
      const data = await coworkService.listConversations();
      set({ conversations: data.conversations || [], isLoadingConversations: false });
    } catch {
      set({ isLoadingConversations: false });
    }
  },

  createNewConversation: async () => {
    // Don't hit API yet — just clear state for a blank conversation.
    // The conversation will be created on first message send.
    set({ activeConversationId: null, messages: [], error: null, panel: { isOpen: false, entityType: null, entityData: null, toolName: null, history: [] } });
  },

  selectConversation: async (id: string) => {
    set({ activeConversationId: id, messages: [], isLoading: true, error: null, panel: { isOpen: false, entityType: null, entityData: null, toolName: null, history: [] } });
    try {
      const rawMessages = await coworkService.getConversationMessages(id);
      const parsed = parseRawMessages(rawMessages);

      // Scan loaded messages for tool_calls to rebuild panel history
      const panelHistory: PanelAction[] = [];
      for (const msg of parsed) {
        if (msg.tool_calls?.length) {
          const action = resolveToolPanel(msg.tool_calls);
          if (action) panelHistory.push(action);
        }
      }

      // Auto-open panel — prefer the last specific entity (agent, KB, training)
      // over list/generic types, so users see the actionable panel (e.g. upload)
      const LIST_TYPES = new Set(['agent_list', 'knowledge_base_list', 'stats', 'generic']);
      const bestAction = panelHistory.length > 0
        ? (panelHistory.slice().reverse().find(a => !LIST_TYPES.has(a.entityType)) || panelHistory[panelHistory.length - 1])
        : null;
      const panel: PanelContext = bestAction
        ? { isOpen: true, entityType: bestAction.entityType, entityData: bestAction.entityData, toolName: bestAction.toolName, history: panelHistory }
        : { isOpen: false, entityType: null, entityData: null, toolName: null, history: panelHistory };

      set({ messages: parsed, isLoading: false, panel });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load messages';
      set({ error: errorMessage, isLoading: false });
    }
  },

  sendMessage: async (message, context) => {
    const state = get();
    let conversationId = state.activeConversationId;

    // If no active conversation, create one first
    if (!conversationId) {
      try {
        const conv = await coworkService.createConversation();
        conversationId = conv.id;
        set({ activeConversationId: conversationId });
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to create conversation';
        set({ error: errorMessage });
        return;
      }
    }

    const userMessage: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: message,
      timestamp: new Date(),
    };

    const assistantId = crypto.randomUUID();
    const assistantMessage: ChatMessage = {
      id: assistantId,
      role: 'assistant',
      content: '',
      activeTools: [],
      isStreaming: true,
      timestamp: new Date(),
    };

    set((s) => ({
      messages: [...s.messages, userMessage, assistantMessage],
      isLoading: true,
      isStreaming: true,
      error: null,
    }));

    const finalConvId = conversationId;

    try {
      const callbacks = buildStreamCallbacks(set, assistantId, () => {
        // After stream completes, reload conversation list to pick up auto-generated title
        get().loadConversations();
      });

      await coworkService.sendMessageStreamInConversation(finalConvId, message, context, callbacks, get().mode);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to send message';
      set((s) => ({
        messages: s.messages.map((m) =>
          m.id === assistantId
            ? { ...m, content: m.content || '*Response interrupted*', isStreaming: false }
            : m
        ),
        error: errorMessage,
        isLoading: false,
        isStreaming: false,
      }));
    } finally {
      set((s) => ({
        isLoading: false,
        isStreaming: false,
        messages: s.messages.map((m) =>
          m.id === assistantId ? { ...m, isStreaming: false, activeTools: [] } : m
        ),
      }));
    }
  },

  deleteConversation: async (id: string) => {
    try {
      await coworkService.deleteConversation(id);
      const state = get();
      const updated = state.conversations.filter((c) => c.id !== id);
      const newState: Partial<CoworkState> = { conversations: updated };
      if (state.activeConversationId === id) {
        newState.activeConversationId = null;
        newState.messages = [];
      }
      set(newState as any);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete conversation';
      set({ error: errorMessage });
    }
  },

  renameConversation: async (id: string, title: string) => {
    try {
      await coworkService.updateTitle(id, title);
      set((state) => ({
        conversations: state.conversations.map((c) =>
          c.id === id ? { ...c, title } : c
        ),
      }));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to rename conversation';
      set({ error: errorMessage });
    }
  },

  clearError: () => set({ error: null }),

  // Legacy compat — delegates to new multi-conversation flow
  loadHistory: async () => {
    await get().loadConversations();
  },

  clearHistory: async () => {
    set({ messages: [], activeConversationId: null });
  },
}));
